// frontend/src/design-system/format.ts

export function formatMoney(amount: string | number, currency: string): string {
  const n = typeof amount === 'string' ? Number(amount) : amount;
  if (!isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(d);
}

/**
 * Format a bare `YYYY-MM-DD` CALENDAR date — a day on a calendar, not an
 * instant in time.
 *
 * Deliberately not `formatDate`: `new Date('2026-07-05')` is parsed as UTC
 * midnight, so a viewer west of UTC sees "Jul 4". For an attendance exception
 * or a leave request the day IS the fact, and the backend has already bucketed
 * it in the tenant's timezone — re-interpreting it through the browser's would
 * report the wrong date. Building the Date from parts pins it to local midnight
 * so the printed day always matches the string.
 */
export function formatCalendarDate(ymd: string | null | undefined): string {
  if (!ymd) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return '—';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime()) || d.getMonth() !== Number(m[2]) - 1) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(d);
}

export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

/**
 * Timezone-aware date/time formatter. Renders an instant in a specific IANA
 * timezone (e.g. the tenant or user's configured tz) rather than the browser's.
 *
 * @param tz   IANA timezone. When omitted/invalid, falls back to the browser tz.
 * @param opts Intl format options (defaults to medium date + short time).
 *
 * Thread `tz` from identity.timezone (user override → tenant default → 'UTC')
 * or tenant.timezone where identity isn't handy.
 */
export function formatInTimeZone(
  iso: string | Date | null | undefined,
  tz?: string | null,
  opts: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '—';
  // Only pass timeZone if it's a non-empty string; otherwise let Intl use the
  // browser default. An invalid tz would throw, so guard with a try/catch.
  try {
    const withTz = tz ? { ...opts, timeZone: tz } : opts;
    return new Intl.DateTimeFormat(undefined, withTz).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, opts).format(d);
  }
}

/** Date-only variant of {@link formatInTimeZone}. */
export function formatDateInTimeZone(
  iso: string | Date | null | undefined,
  tz?: string | null,
): string {
  return formatInTimeZone(iso, tz, { dateStyle: 'medium' });
}

/** Days from today to the given date. Negative when overdue. */
export function daysUntil(iso: string | Date | null | undefined): number | null {
  if (!iso) return null;
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tgt = new Date(d);
  tgt.setHours(0, 0, 0, 0);
  return Math.round((tgt.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}
